import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { 
  Edit, 
  BookOpen, 
  Calendar,
  User,
  GraduationCap,
  Target,
  Clock,
  TrendingUp,
  Lock,
  AlertTriangle,
  Bell
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { pb } from '@/lib/pocketbase';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getUserInitials, getProfilePictureUrl } from '@/utils/profileUtils';
import EditStudentProfileModal from '@/components/EditStudentProfileModal';
import SuccessStoryForm from '@/components/SuccessStoryForm';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import ProfileSuspensionModal from '@/components/ProfileSuspensionModal';
import { SubscriptionSection } from '@/components/profile/SubscriptionSection';

interface StudentProfile {
  id: string;
  user: string;
  name: string;
  bio?: string;
  grade?: string;
  subjects: string[];
  learningGoals?: string;
  preferredLearningStyle?: string;
  parentEmail?: string;
  emergencyContact?: string;
  profilePicture?: string;
  profilePictureUrl?: string;
  created: string;
  updated: string;
  collectionId?: string;
  collectionName?: string;
  expand?: {
    user?: any;
  };
}

interface StudentProfileProps {
  profileUser?: any; // User data passed from ProfileRouter
}

const StudentProfile = ({ profileUser }: StudentProfileProps) => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isSuspensionModalOpen, setIsSuspensionModalOpen] = useState(false);

  useEffect(() => {
    const loadStudentProfile = async () => {
      
      
      setDataLoaded(false);
      
      let targetUserId: string;
      
      // Use profileUser if provided, otherwise fall back to current logic
      if (profileUser) {
        targetUserId = profileUser.id;
      } else if (!id) {
        if (!user || user.role !== 'student') {
          
          setProfile(null);
          setDataLoaded(true);
          setIsLoading(false);
          return;
        }
        targetUserId = user.id;
      } else {
        targetUserId = id;
      }

      try {
        const records = await pb.collection('student_profiles').getList(1, 1, {
          filter: `user = "${targetUserId}"`,
          expand: 'user'
        });
        
        if (records.items.length > 0) {
          const profileData = records.items[0];
          const studentProfile: StudentProfile = {
            id: profileData.id,
            user: profileData.user,
            name: profileData.name || '',
            bio: profileData.bio || '',
            grade: profileData.grade || '',
            subjects: profileData.subjects || [],
            learningGoals: profileData.learningGoals || '',
            preferredLearningStyle: profileData.preferredLearningStyle || '',
            parentEmail: profileData.parentEmail || '',
            emergencyContact: profileData.emergencyContact || '',
            profilePicture: profileData.profilePicture || '',
            profilePictureUrl: profileData.profilePictureUrl || '',
            collectionId: profileData.collectionId,
            collectionName: profileData.collectionName,
            created: profileData.created,
            updated: profileData.updated,
            expand: profileData.expand
          };
          
          setProfile(studentProfile);
        } else {
          // If viewing someone else's profile and no profile exists, show empty state
          if (profileUser && profileUser.id !== user?.id) {
            setProfile(null);
          } else if (!id && user?.id === targetUserId) {
            
            setProfile(null);
          } else {
            
            setProfile(null);
          }
        }
      } catch (error) {
        
        setProfile(null);
      } finally {
        setDataLoaded(true);
        setIsLoading(false);
      }
    };

    loadStudentProfile();
  }, [id, user, profileUser]);

  const isOwnProfile = user && (profileUser ? user.id === profileUser.id : (!id || user.id === id));
  const isStudent = user?.role === 'student';
  const isViewingOtherProfile = profileUser && user?.id !== profileUser.id;

  const createDefaultProfile = async () => {
    if (!user || user.role !== 'student') return;
    
    try {
      const newProfileData = await pb.collection('student_profiles').create({
        user: user.id,
        name: user.name || '',
        bio: '',
        grade: '',
        subjects: [],
        learningGoals: '',
        preferredLearningStyle: '',
        parentEmail: '',
        emergencyContact: '',
        is_suspended: false, // Ensure profile is not suspended by default
        suspended_at: null // No suspension date
      });
      
      const studentProfile: StudentProfile = {
        id: newProfileData.id,
        user: newProfileData.user,
        name: newProfileData.name || '',
        bio: newProfileData.bio || '',
        grade: newProfileData.grade || '',
        subjects: newProfileData.subjects || [],
        learningGoals: newProfileData.learningGoals || '',
        preferredLearningStyle: newProfileData.preferredLearningStyle || '',
        parentEmail: newProfileData.parentEmail || '',
        emergencyContact: newProfileData.emergencyContact || '',
        created: newProfileData.created,
        updated: newProfileData.updated
      };
      
      setProfile(studentProfile);
    } catch (error: any) {
      console.error('Error creating student profile:', error);
      
      // Extract meaningful error message
      let errorMessage = "Failed to create profile";
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.response?.message) {
        errorMessage = error.response.message;
      }
      
      toast({
        title: "Error Creating Profile",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleProfileUpdate = () => {
    // Reload the profile data after successful update
    const reloadProfile = async () => {
      if (!user) return;
      
      try {
        const records = await pb.collection('student_profiles').getList(1, 1, {
          filter: `user = "${user.id}"`,
          expand: 'user'
        });
        
        if (records.items.length > 0) {
          const profileData = records.items[0];
          const studentProfile: StudentProfile = {
            id: profileData.id,
            user: profileData.user,
            name: profileData.name || '',
            bio: profileData.bio || '',
            grade: profileData.grade || '',
            subjects: profileData.subjects || [],
            learningGoals: profileData.learningGoals || '',
            preferredLearningStyle: profileData.preferredLearningStyle || '',
            parentEmail: profileData.parentEmail || '',
            emergencyContact: profileData.emergencyContact || '',
            profilePicture: profileData.profilePicture || '',
            profilePictureUrl: profileData.profilePictureUrl || '',
            collectionId: profileData.collectionId,
            collectionName: profileData.collectionName,
            created: profileData.created,
            updated: profileData.updated,
            expand: profileData.expand
          };
          setProfile(studentProfile);
        }
      } catch (error) {
        
      }
    };
    
    reloadProfile();
  };

  // Show loading while data is being fetched
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start space-x-6">
              <div className="w-32 h-32 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show not found only after data loading is complete and no data exists
  if (dataLoaded && !profile && !isOwnProfile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Student Profile Not Found</h1>
          <p className="text-gray-600">
            {isViewingOtherProfile 
              ? `${profileUser?.name || 'This student'} hasn't set up their profile yet.` 
              : 'The student profile you\'re looking for doesn\'t exist.'
            }
          </p>
        </div>
      </div>
    );
  }

  // Show create profile option for current user
  if (dataLoaded && !profile && isOwnProfile && isStudent) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <Card>
            <CardContent className="pt-6">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h2 className="text-2xl font-bold mb-2">Create Your Student Profile</h2>
              <p className="text-gray-600 mb-6">
                Set up your learning profile to help tutors understand your goals and preferences.
              </p>
              <Button onClick={createDefaultProfile}>
                <Edit className="w-4 h-4 mr-2" />
                Create Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const displayName = profile?.name || profileUser?.name || user?.name || 'Student';
  const displayBio = profile?.bio || 'Student learning profile';

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white shadow-lg">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Avatar className="w-32 h-32">
                <AvatarImage 
                  src={getProfilePictureUrl(profile)} 
                  alt={displayName}
                />
                <AvatarFallback className="text-2xl">
                  {getUserInitials(displayName)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="text-3xl font-bold text-gray-900">
                      {displayName}
                    </CardTitle>
                    <p className="text-lg text-gray-600 mt-1">
                      {displayBio}
                    </p>
                    {profile?.grade && (
                      <Badge variant="secondary" className="mt-2">
                        Grade {profile.grade}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {isOwnProfile && isStudent && profile && (
                      <>
                        <Button variant="outline" className="w-full sm:w-auto" onClick={() => setIsEditModalOpen(true)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                        <Button variant="outline" className="w-full sm:w-auto" onClick={() => setIsChangePasswordModalOpen(true)}>
                          <Lock className="w-4 h-4 mr-2" />
                          Password
                        </Button>
                        <Button variant="destructive" className="w-full sm:w-auto" onClick={() => setIsSuspensionModalOpen(true)}>
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Suspend/Delete Profile
                        </Button>
                        <Link to="/settings">
                          <Button variant="outline" className="w-full sm:w-auto">
                            <Bell className="w-4 h-4 mr-2" />
                            Push Notification
                          </Button>
                        </Link>
                        <SuccessStoryForm />
                      </>
                    )}
                  </div>
                  
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Subscription Section - Only show for own profile */}
            {isOwnProfile && (
              <SubscriptionSection />
            )}

            {isOwnProfile && <Separator />}

            {/* Bio Section */}
            {profile?.bio && (
              <div>
                <h3 className="text-xl font-semibold mb-3">About</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {profile.bio}
                </p>
              </div>
            )}

            <Separator />

            {/* Key Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subjects of Interest */}
              {profile?.subjects && profile.subjects.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Subjects of Interest
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.subjects.map((subject: string) => (
                      <Badge key={subject} variant="secondary">
                        {subject}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Learning Goals */}
              {profile?.learningGoals && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <Target className="w-5 h-5 mr-2" />
                    Learning Goals
                  </h3>
                  <p className="text-gray-700">{profile.learningGoals}</p>
                </div>
              )}

              {/* Preferred Learning Style */}
              {profile?.preferredLearningStyle && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Learning Style
                  </h3>
                  <p className="text-gray-700">{profile.preferredLearningStyle}</p>
                </div>
              )}

              {/* Grade Level */}
              {profile?.grade && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2" />
                    Grade Level
                  </h3>
                  <p className="text-2xl font-bold text-blue-600">
                    Grade {profile.grade}
                  </p>
                </div>
              )}
            </div>

            {/* Contact Information (only visible to own profile) */}
            {isOwnProfile && (profile?.parentEmail || profile?.emergencyContact) && (
              <>
                <Separator />
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.parentEmail && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">Parent Email</h4>
                        <p className="text-gray-600">{profile.parentEmail}</p>
                      </div>
                    )}
                    {profile.emergencyContact && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">Emergency Contact</h4>
                        <p className="text-gray-600">{profile.emergencyContact}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      {profile && (
        <EditStudentProfileModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          studentData={profile}
          onSave={handleProfileUpdate}
        />
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      {/* Profile Suspension Modal */}
      <ProfileSuspensionModal
        isOpen={isSuspensionModalOpen}
        onClose={() => setIsSuspensionModalOpen(false)}
      />
    </div>
  );
};

export default StudentProfile;
